import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store as NgxsStore, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState, UserStateModel } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { StoreActions } from '../actions/store.actions';
import { Store } from '../models/store.model';
import { StoreService } from 'src/app/services/store.service';
import { StorageService } from 'src/app/services/storage.service';
import { DateTime } from 'luxon';

export class StoreStateModel {
    is_refreshing: boolean
    last_update: string
    store: Store[]
    user: User
}

@State<StoreStateModel>({
    name: 'store',
    defaults: {
        is_refreshing: true,
        last_update: '',
        store: [],
        user: UserStateModel.getDefaultUser()
    }
})
@Injectable()
export class StoreState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private storeService: StoreService,
        private store: NgxsStore,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<StoreStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {
                        await this.reloadStateFromStorage(user)                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {       
        if (this.storageService.is_initialized) {
            const state_model: StoreStateModel = change.currentValue
            this.storageService.set('stores', JSON.stringify(state_model))    
        }      
    }

    async reloadStateFromStorage(user: User) : Promise<void> {        
        const json = await this.storageService.get('stores');        
        if (json) {
            const state_model: StoreStateModel = JSON.parse(json)
            if (state_model) {
                state_model.user = user
                this.store.dispatch(new StoreActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new StoreActions.Refresh(user))
    }

    async reloadStateFromApi(user: User, last_update: string) : Promise<boolean> {    
        const objs = await this.storeService.getMine(user, last_update)    
        if (objs != null) {
          const many = Store.buildMany(objs)                  
          this.store.dispatch(new StoreActions.Set({ stores: many, user }))
          return true
        }
        return false
    }

    @Selector()
    static getLastUpdate(state: StoreStateModel) {
        return state.last_update
    }

  
    @Selector()
    static getState(state: StoreStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: StoreStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: StoreStateModel) {
        return state.store.filter(f => !f.is_deleted);
    }

    @Action(StoreActions.Refresh)
    async refresh({getState, patchState}: StateContext<StoreStateModel>, { payload }:StoreActions.Refresh) {
        const state = getState()
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi(payload, state.last_update)) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(StoreActions.Init)
    init({setState, getState}: StateContext<StoreStateModel>, { payload }:StoreActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new StoreActions.Refresh(payload.user))
    }

    @Action(StoreActions.Set)
    set({ getState, setState }: StateContext<StoreStateModel>, { payload }: StoreActions.Set) {
        const current = getState();

        const map = new Map(current.store.map(s => [s.id, s]));

        for (const incoming of payload.stores) {
            map.set(incoming.id, incoming);
        }

        setState({
            is_refreshing: false,
            last_update: DateTime.now().toISO() as string,
            store: Array.from(map.values()),
            user: payload.user
        });
    }

}
